// assets/engraving-cart-sync.js
//
// Hält Messer und Gravurprodukt im Warenkorb konsistent – egal ob der Warenkorb über Horizon,
// die Warenkorb-Seite oder eine App (z. B. Sticky Cart by Qikify) geändert wird.
//
// Verknüpfung (gesetzt von assets/knife-engraving.js):
//   Messer/Bundle:  properties._gravur_id
//   Gravurprodukt:  properties._gravur_ref
//   Seitenanzahl:   Anzahl der "Gravur – …"-Eigenschaften am Messer
//
// Regeln:
//   1. Gravur ohne zugehöriges Messer (z. B. Bundle entfernt)  → Gravur entfernen
//   2. Gravurmenge ≠ Messermenge × Seiten                       → Gravurmenge korrigieren
//   3. Messer mit Gravurtext, aber Gravur wurde gelöscht         → Gravurangaben vom Messer entfernen

const CART_REQUEST = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const ENGRAVING_PROPERTY = /^gravur\b/i;
const FONT_PROPERTY = 'Schriftart';
const ROOT = window.Shopify?.routes?.root ?? '/';

let running = false;
let pending = false;
let timer = 0;
let retries = 0;
const MAX_RETRIES = 2;

/** @param {string} url @param {object} body */
async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url}: ${response.status}`);
  return response.json();
}

/** @param {string} key */
function isEngravingKey(key) {
  return ENGRAVING_PROPERTY.test(key) || key === FONT_PROPERTY || key.startsWith('_gravur');
}

/** @param {any} knife Anzahl gravierter Seiten = Anzahl "Gravur – …"-Eigenschaften */
function sidesFor(knife) {
  const counted = Object.keys(knife.properties ?? {}).filter((key) => ENGRAVING_PROPERTY.test(key)).length;
  return counted || 1;
}

async function sync() {
  if (running) {
    pending = true;
    return;
  }
  running = true;
  let changed = false;

  try {
    const cart = await (await fetch(`${ROOT}cart.js`, { cache: 'no-store' })).json();
    const items = /** @type {any[]} */ (cart.items ?? []);

    const knives = new Map(items.filter((item) => item.properties?._gravur_id).map((item) => [item.properties._gravur_id, item]));
    const engravings = items.filter((item) => item.properties?._gravur_ref);
    const engravedIds = new Set(engravings.map((item) => item.properties._gravur_ref));

    for (const engraving of engravings) {
      const knife = knives.get(engraving.properties._gravur_ref);

      // 1. Verwaiste Gravur entfernen
      if (!knife) {
        await post(`${ROOT}cart/change.js`, { id: engraving.key, quantity: 0 });
        changed = true;
        continue;
      }

      // 2. Menge angleichen
      const expected = knife.quantity * sidesFor(knife);
      if (engraving.quantity !== expected) {
        await post(`${ROOT}cart/change.js`, { id: engraving.key, quantity: expected });
        changed = true;
      }
    }

    // 3. Gravur wurde gelöscht → Messer ohne Gravurangaben weiterführen.
    //    Eigenschaften lassen sich per cart/change nicht entfernen (nur leeren), daher wird das Messer
    //    ohne Gravurangaben neu eingelegt. Erst entfernen, dann hinzufügen – so kann eine Wiederholung
    //    nach einem Fehler das Messer nie doppelt in den Warenkorb legen.
    for (const [id, knife] of knives) {
      if (engravedIds.has(id)) continue;
      const properties = Object.fromEntries(
        Object.entries(knife.properties ?? {}).filter(([key]) => !isEngravingKey(key))
      );
      await post(`${ROOT}cart/change.js`, { id: knife.key, quantity: 0 });
      changed = true;
      await post(`${ROOT}cart/add.js`, {
        items: [{ id: knife.variant_id, quantity: knife.quantity, ...(Object.keys(properties).length ? { properties } : {}) }],
      });
    }
    retries = 0;
  } catch (error) {
    // Meist hat sich der Warenkorb zwischen Lesen und Ändern geändert (z. B. Zeile schon entfernt) –
    // dann mit frischem Stand erneut prüfen.
    if (retries < MAX_RETRIES) {
      retries += 1;
      pending = true;
    } else {
      retries = 0;
      console.warn('[engraving-cart-sync]', error);
    }
  } finally {
    running = false;
  }

  if (changed) await refreshThemeCart();
  if (pending) {
    pending = false;
    schedule();
  }
}

/** Horizon-Warenkorb (Drawer/Seite) neu rendern. Qikify aktualisiert sich bei Warenkorb-Anfragen selbst. */
async function refreshThemeCart() {
  const components = document.querySelectorAll('cart-items-component[data-section-id]');
  if (components.length === 0) return;

  try {
    const { sectionRenderer } = await import('@theme/section-renderer');
    for (const component of components) {
      const sectionId = /** @type {HTMLElement} */ (component).dataset.sectionId;
      if (sectionId) await sectionRenderer.renderSection(sectionId, { cache: false });
    }
  } catch (error) {
    // Fallback auf der Warenkorb-Seite: neu laden, damit Menge und Preis stimmen.
    if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
  }
}

function schedule() {
  clearTimeout(timer);
  timer = window.setTimeout(sync, 250);
}

// Jede abgeschlossene Warenkorb-Anfrage (Theme, Qikify, Warenkorb-Seite) löst eine Prüfung aus –
// ohne fremden Code zu verändern.
if ('PerformanceObserver' in window) {
  new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const type = /** @type {PerformanceResourceTiming} */ (entry).initiatorType;
      if ((type === 'fetch' || type === 'xmlhttprequest') && CART_REQUEST.test(entry.name)) {
        schedule();
        return;
      }
    }
  }).observe({ type: 'resource', buffered: false });
}

// Beim Laden (und bei Rückkehr aus dem Checkout per Zurück-Taste) einmal prüfen.
schedule();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) schedule();
});
